// SUN-105: the "Open page" review bar. Starts when an admin presses Open page on a page-note card:
// notes mode on → go to the note's page → ring its dot (the dot only exists on the writer's own device, so a miss
// shows the note text) → the bar offers Approve · Reject · Next note · Back to board. Next note walks the
// approval queue (GET /page-notes/approval-queue). Mounted in AppShell next to PageNotesGate.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ApprovalNote } from '../types';
import { errorText } from '../mockupBoardApi';
import { pnApi } from './pageNotesApi';
import { safeRoute } from './annotation';
import { focusNote } from './focusNote';
import { setPageNotesMode } from './pageNotesMode';
import { endReview, useReview } from './reviewStore';
import { PageNoteRejectModal } from './PageNoteRejectModal';

const FOCUS_DELAY_MS = 400;
export const BOARD_PENDING = '/mockupboard?tab=pending';
const firstName = (n: string) => n.trim().split(/\s+/)[0] || n;
const barBtn = 'rounded-full px-3 py-1 text-[13px] font-semibold';

export function ReviewBar() {
  const start = useReview();
  if (!start) return null;
  return <Bar key={start.id} start={start} />;
}

function Bar({ start }: { start: ApprovalNote }) {
  const nav = useNavigate();
  const loc = useLocation();
  const locRef = useRef(loc);
  locRef.current = loc;
  const [queue, setQueue] = useState<ApprovalNote[]>([start]);
  const [idx, setIdx] = useState(0);
  const [miss, setMiss] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const decidable = start.decision === 'pending';

  // The queue: all pending notes; the one that was opened is where the bar starts.
  useEffect(() => {
    if (!decidable) return;
    let live = true;
    pnApi.approvalQueue().then(list => {
      if (!live || !Array.isArray(list)) return;
      const i = list.findIndex(n => n.id === start.id);
      if (i >= 0) { setQueue(list); setIdx(i); } else setQueue([start, ...list]);
    }).catch(() => { /* keep the single note */ });
    return () => { live = false; };
  }, [decidable, start]);

  const cur = queue[Math.min(idx, queue.length - 1)] ?? start;

  // Go to the note's page and ring its dot.
  useEffect(() => {
    setPageNotesMode(true);
    setMiss(false);
    const to = safeRoute(cur.route);
    const here = locRef.current.pathname + locRef.current.search;
    const path = to ? to.split('?')[0]! : locRef.current.pathname;
    const moved = !!to && to !== here;
    if (moved) nav(to!);
    const t = setTimeout(() => setMiss(!focusNote(cur.clientId, { pathname: path })), moved ? FOCUS_DELAY_MS : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur.id]);

  const back = () => { endReview(); setPageNotesMode(false); nav(BOARD_PENDING); };
  const afterDecision = (id: string) => {
    const rest = queue.filter(n => n.id !== id);
    if (rest.length === 0) { back(); return; }
    setQueue(rest);
    setIdx(i => Math.min(i, rest.length - 1));
  };
  const approve = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try { await pnApi.approve(cur.id); afterDecision(cur.id); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div role="region" aria-label="Page note review" data-feedback-toolbar=""
        className="fixed left-1/2 top-3 z-[2147483000] flex w-[min(720px,calc(100vw-24px))] -translate-x-1/2 flex-col gap-2 rounded-2xl border border-[#E2DCC8] bg-[#FEFCF7] p-3 text-[#1B1713] shadow-2xl">
        <p className="whitespace-pre-wrap break-words text-[13px]">
          <span className="font-semibold">{`${firstName(cur.authorName)}'s note ${Math.min(idx, queue.length - 1) + 1} of ${queue.length}`}</span>
          {` — ${cur.comment}`}
        </p>
        {miss && <p className="text-[12px] text-[#5E564B]">This note was written on another screen, so its dot is not here.</p>}
        {error && <p role="alert" className="text-[12px] text-red-600">{error}</p>}
        <div className="flex flex-wrap items-center gap-2">
          {decidable && (
            <>
              <button type="button" disabled={busy} onClick={() => void approve()} className={`${barBtn} bg-green-600 text-white hover:bg-green-700 disabled:opacity-60`}>Approve</button>
              <button type="button" disabled={busy} onClick={() => setRejecting(true)} className={`${barBtn} bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-60`}>Reject</button>
              <button type="button" disabled={queue.length < 2} onClick={() => setIdx(i => (i + 1) % queue.length)} className={`${barBtn} bg-[#F4F0E4] text-[#1B1713] disabled:opacity-50`}>Next note</button>
            </>
          )}
          <button type="button" onClick={back} className={`${barBtn} ml-auto text-[#5E564B] hover:bg-[#F4F0E4]`}>Back to board</button>
        </div>
      </div>
      {rejecting && <PageNoteRejectModal note={cur} onClose={() => setRejecting(false)} onDone={() => { setRejecting(false); afterDecision(cur.id); }} />}
    </>
  );
}
